/**
 * Custom hook for Chat Widget
 * Handles all state management and API calls for the chat widget
 */

import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useAuth } from '@/store/authStore';
import api from '@/services/api/client';
import toast from 'react-hot-toast';
import type {
  SupportTicket,
  Broadcast,
  Conversation,
  ChatMessage,
  ChatUser,
  FaqItem,
  TabType,
  SupportView,
  ChatView,
} from '@/types/chat';

interface UseChatWidgetReturn {
  // UI State
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
  activeTab: TabType;
  setActiveTab: (tab: TabType) => void;
  loading: boolean;
  
  // Position & Dragging
  position: { x: number; y: number };
  isDragging: boolean;
  panelPosition: { left: number; top: number };
  handleDragStart: (e: React.MouseEvent | React.TouchEvent) => void;
  handleButtonClick: () => void;
  buttonRef: React.RefObject<HTMLButtonElement>;
  
  // Support
  supportView: SupportView;
  setSupportView: (view: SupportView) => void;
  tickets: SupportTicket[];
  selectedTicket: SupportTicket | null;
  setSelectedTicket: (ticket: SupportTicket | null) => void;
  newTicketSubject: string;
  setNewTicketSubject: (subject: string) => void;
  newTicketMessage: string;
  setNewTicketMessage: (message: string) => void;
  newTicketPhone: string;
  setNewTicketPhone: (phone: string) => void;
  newTicketEmail: string;
  setNewTicketEmail: (email: string) => void;
  newTicketRegNumber: string;
  setNewTicketRegNumber: (reg: string) => void;
  newTicketBookingNumber: string;
  setNewTicketBookingNumber: (booking: string) => void;
  ticketReply: string;
  setTicketReply: (reply: string) => void;
  handleCreateTicket: () => Promise<void>;
  handleTicketReply: () => Promise<void>;
  handleCloseTicket: () => Promise<void>;
  
  // Chat
  chatView: ChatView;
  setChatView: (view: ChatView) => void;
  conversations: Conversation[];
  chatUsers: ChatUser[];
  selectedChatUser: { id: string; name: string } | null;
  chatMessages: ChatMessage[];
  newChatMessage: string;
  setNewChatMessage: (message: string) => void;
  chatSearch: string;
  setChatSearch: (search: string) => void;
  isSearching: boolean;
  handleStartChat: (chatUser: { id: string; name: string }) => Promise<void>;
  handleSendChatMessage: () => Promise<void>;
  handleCloseChat: () => Promise<void>;
  
  // Broadcasts
  broadcasts: Broadcast[];
  handleMarkBroadcastRead: (broadcastId: string) => Promise<void>;
  
  // FAQ
  faqItems: FaqItem[];
  
  // Counts
  unreadTickets: number;
  unreadChats: number;
  unreadBroadcasts: number;
  totalUnread: number;
  
  // Helpers
  formatDate: (dateStr: string) => string;
  messagesEndRef: React.RefObject<HTMLDivElement>;
  supportContact: { visible: boolean; name?: string; email?: string; phone?: string };
}

export const useChatWidget = (t: (key: string) => string): UseChatWidgetReturn => {
  const { user } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<TabType>('support');
  
  // Support state
  const [supportView, setSupportView] = useState<SupportView>('list');
  const [tickets, setTickets] = useState<SupportTicket[]>([]);
  const [selectedTicket, setSelectedTicket] = useState<SupportTicket | null>(null);
  const [newTicketSubject, setNewTicketSubject] = useState('');
  const [newTicketMessage, setNewTicketMessage] = useState('');
  const [newTicketPhone, setNewTicketPhone] = useState('');
  const [newTicketEmail, setNewTicketEmail] = useState('');
  const [newTicketRegNumber, setNewTicketRegNumber] = useState('');
  const [newTicketBookingNumber, setNewTicketBookingNumber] = useState('');
  const [ticketReply, setTicketReply] = useState('');
  const [supportContact, setSupportContact] = useState<{
    visible: boolean;
    name?: string;
    email?: string;
    phone?: string;
  }>({ visible: false });
  
  // Chat state
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [chatUsers, setChatUsers] = useState<ChatUser[]>([]);
  const [selectedChatUser, setSelectedChatUser] = useState<{ id: string; name: string } | null>(null);
  const [chatMessages, setChatMessages] = useState<ChatMessage[]>([]);
  const [newChatMessage, setNewChatMessage] = useState('');
  const [chatView, setChatView] = useState<ChatView>('list');
  const [chatSearch, setChatSearch] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const searchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  
  // Broadcast state
  const [broadcasts, setBroadcasts] = useState<Broadcast[]>([]);
  
  // FAQ state
  const [faqItems, setFaqItems] = useState<FaqItem[]>([]);
  
  // Counts
  const [unreadTickets, setUnreadTickets] = useState(0);
  const [unreadChats, setUnreadChats] = useState(0);
  const [unreadBroadcasts, setUnreadBroadcasts] = useState(0);
  
  const [loading, setLoading] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  
  // Draggable position state
  const [position, setPosition] = useState<{ x: number; y: number }>(() => {
    const defaultPos = { 
      x: Math.max(0, (typeof window !== 'undefined' ? window.innerWidth : 800) - 80), 
      y: Math.max(0, (typeof window !== 'undefined' ? window.innerHeight : 600) - 120) 
    };
    
    const saved = localStorage.getItem('chatWidgetPosition');
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        const maxX = (typeof window !== 'undefined' ? window.innerWidth : 800) - 56;
        const maxY = (typeof window !== 'undefined' ? window.innerHeight : 600) - 56;
        if (parsed.x >= 0 && parsed.x <= maxX && parsed.y >= 0 && parsed.y <= maxY) {
          return parsed;
        }
      } catch {
        // Ignore parse errors
      }
    }
    return defaultPos;
  });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const buttonRef = useRef<HTMLButtonElement>(null);

  // Ensure position is always visible on resize
  useEffect(() => {
    const handleResize = () => {
      setPosition(prev => ({
        x: Math.min(prev.x, window.innerWidth - 56),
        y: Math.min(prev.y, window.innerHeight - 56),
      }));
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (user?.email) {
      setNewTicketEmail((prev) => prev || user.email);
    }
    if (user?.MobilePhone) {
      setNewTicketPhone((prev) => prev || user.MobilePhone || '');
    }
  }, [user]);

  // Load data on open
  useEffect(() => {
    if (isOpen) {
      loadData();
    }
  }, [isOpen, activeTab]);

  // Check for unread messages on mount
  useEffect(() => {
    const checkUnreadCounts = async () => {
      try {
        const ticketsRes = await api.get('/api/chat/support/tickets');
        if (ticketsRes.data.success) {
          setUnreadTickets(ticketsRes.data.data.filter((t: SupportTicket) => 
            t.status === 'waiting_customer'
          ).length);
        }

        const chatsRes = await api.get('/api/chat/unread');
        if (chatsRes.data.success) {
          setUnreadChats(chatsRes.data.data.count || 0);
        }

        const broadcastsRes = await api.get('/api/chat/broadcasts/unread');
        if (broadcastsRes.data.success) {
          setUnreadBroadcasts(broadcastsRes.data.data.count || 0);
        }
      } catch (error) {
        console.debug('Could not check unread counts:', error);
      }
    };

    checkUnreadCounts();
    const interval = setInterval(checkUnreadCounts, 60000);
    return () => clearInterval(interval);
  }, [user?.id]);

  // Save position to localStorage
  useEffect(() => {
    localStorage.setItem('chatWidgetPosition', JSON.stringify(position));
  }, [position]);

  // Handle drag start
  const handleDragStart = useCallback((e: React.MouseEvent | React.TouchEvent) => {
    e.preventDefault();
    setIsDragging(true);
    
    const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX;
    const clientY = 'touches' in e ? e.touches[0].clientY : e.clientY;
    
    setDragStart({
      x: clientX - position.x,
      y: clientY - position.y,
    });
  }, [position]);

  // Handle drag move
  useEffect(() => {
    if (!isDragging) return;

    const handleMove = (e: MouseEvent | TouchEvent) => {
      const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX;
      const clientY = 'touches' in e ? e.touches[0].clientY : e.clientY;
      
      const newX = Math.max(0, Math.min(window.innerWidth - 56, clientX - dragStart.x));
      const newY = Math.max(0, Math.min(window.innerHeight - 56, clientY - dragStart.y));
      
      setPosition({ x: newX, y: newY });
    };

    const handleEnd = () => {
      setIsDragging(false);
    };

    document.addEventListener('mousemove', handleMove);
    document.addEventListener('mouseup', handleEnd);
    document.addEventListener('touchmove', handleMove);
    document.addEventListener('touchend', handleEnd);

    return () => {
      document.removeEventListener('mousemove', handleMove);
      document.removeEventListener('mouseup', handleEnd);
      document.removeEventListener('touchmove', handleMove);
      document.removeEventListener('touchend', handleEnd);
    };
  }, [isDragging, dragStart]);

  // Handle click (only if not dragging)
  const handleButtonClick = useCallback(() => {
    if (!isDragging) {
      setIsOpen(!isOpen);
    }
  }, [isDragging, isOpen]);

  // Calculate panel position based on button position
  const panelPosition = useMemo(() => {
    const buttonCenterX = position.x + 28;
    const panelWidth = 384;
    const panelHeight = 500;
    
    let left = Math.max(8, Math.min(window.innerWidth - panelWidth - 8, position.x - panelWidth + 56));
    let top = position.y - panelHeight - 16;
    
    if (top < 8) {
      top = position.y + 56 + 16;
    }
    
    if (buttonCenterX < window.innerWidth / 2) {
      left = Math.max(8, position.x);
    }
    
    return { left, top };
  }, [position]);

  // Scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [selectedTicket, chatMessages]);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      
      if (activeTab === 'support') {
        const [res, contactRes] = await Promise.all([
          api.get('/api/chat/support/tickets'),
          api.get('/api/chat/support/contact').catch(() => null),
        ]);
        if (res.data.success) {
          setTickets(res.data.data);
          setUnreadTickets(res.data.data.filter((t: SupportTicket) => 
            t.status === 'waiting_customer'
          ).length);
        }
        if (contactRes?.data?.success && contactRes.data.data) {
          setSupportContact(contactRes.data.data);
        }
      } else if (activeTab === 'chat') {
        const [convRes, usersRes] = await Promise.all([
          api.get('/api/chat/conversations'),
          api.get('/api/chat/users'),
        ]);
        if (convRes.data.success) {
          setConversations(convRes.data.data);
          setUnreadChats(convRes.data.data.reduce((sum: number, c: Conversation) => 
            sum + c.unread_count, 0
          ));
        }
        if (usersRes.data.success) {
          setChatUsers(usersRes.data.data);
        }
      } else if (activeTab === 'broadcasts') {
        const res = await api.get('/api/chat/broadcasts');
        if (res.data.success) {
          setBroadcasts(res.data.data);
          setUnreadBroadcasts(res.data.data.filter((b: Broadcast) => 
            !b.read_by.includes(user?.id || '')
          ).length);
        }
      } else if (activeTab === 'faq') {
        const res = await api.get('/api/chat/faq');
        if (res.data.success) {
          setFaqItems(res.data.data);
        }
      }
    } catch (error) {
      console.error('Error loading chat data:', error);
    } finally {
      setLoading(false);
    }
  }, [activeTab, user?.id]);

  // Create new support ticket
  const handleCreateTicket = async () => {
    if (!newTicketSubject.trim() || !newTicketMessage.trim()) {
      toast.error(t('chat.fillAllFields') || 'Fyll i alla fält');
      return;
    }

    try {
      const res = await api.post('/api/chat/support/tickets', {
        subject: newTicketSubject,
        message: newTicketMessage,
        priority: 'medium',
        contact_phone: newTicketPhone.trim() || undefined,
        contact_email: newTicketEmail.trim() || undefined,
        registration_number: newTicketRegNumber.trim() || undefined,
        booking_number: newTicketBookingNumber.trim() || undefined,
      });

      if (res.data.success) {
        toast.success(t('chat.ticketCreated') || 'Ärende skapat');
        setNewTicketSubject('');
        setNewTicketMessage('');
        setNewTicketPhone('');
        setNewTicketEmail(user?.email || '');
        setNewTicketPhone(user?.MobilePhone || '');
        setNewTicketRegNumber('');
        setNewTicketBookingNumber('');
        setSupportView('list');
        loadData();
      }
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Kunde inte skapa ärende');
    }
  };

  // Reply to ticket
  const handleTicketReply = async () => {
    if (!ticketReply.trim() || !selectedTicket) return;

    try {
      const res = await api.post(`/api/chat/support/tickets/${selectedTicket.ticket_id}/reply`, {
        message: ticketReply,
      });

      if (res.data.success) {
        setSelectedTicket(res.data.data);
        setTicketReply('');
      }
    } catch (error: any) {
      toast.error(t('support.sendReplyError'));
    }
  };

  // Close support ticket
  const handleCloseTicket = async () => {
    if (!selectedTicket) return;
    
    try {
      const res = await api.post(`/api/chat/support/tickets/${selectedTicket.ticket_id}/reply`, {
        message: t('chat.ticketClosedByUser') || 'Ärendet stängdes av användaren.',
      });

      if (res.data.success) {
        const updatedTicket = { ...res.data.data, status: 'closed' as const };
        setSelectedTicket(updatedTicket);
        setTickets(tickets.map(t => 
          t.ticket_id === selectedTicket.ticket_id ? updatedTicket : t
        ));
        toast.success(t('chat.ticketClosed') || 'Ärendet har stängts');
      }
    } catch (error: any) {
      toast.error(t('support.closeTicketError'));
    }
  };

  // Mark broadcast as read
  const handleMarkBroadcastRead = async (broadcastId: string) => {
    try {
      await api.post(`/api/chat/broadcasts/${broadcastId}/read`);
      setBroadcasts(broadcasts.map(b => 
        b.broadcast_id === broadcastId 
          ? { ...b, read_by: [...b.read_by, user?.id || ''] }
          : b
      ));
      setUnreadBroadcasts(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error('Error marking broadcast as read:', error);
    }
  };

  // Start chat with user
  const handleStartChat = async (chatUser: { id: string; name: string }) => {
    setSelectedChatUser(chatUser);
    setChatView('conversation');
    
    const conversationId = [user?.id, chatUser.id].sort().join('_');
    
    try {
      const res = await api.get(`/api/chat/messages/${conversationId}`);
      if (res.data.success) {
        setChatMessages(res.data.data.reverse());
      }
    } catch (error) {
      console.error('Error loading messages:', error);
      setChatMessages([]);
    }
    
    try {
      await api.post('/api/chat/read', { conversation_id: conversationId });
      
      setConversations(prev => prev.map(c => 
        c.conversation_id === conversationId 
          ? { ...c, unread_count: 0 }
          : c
      ));
      
      setUnreadChats(prev => {
        const conv = conversations.find(c => c.conversation_id === conversationId);
        return Math.max(0, prev - (conv?.unread_count || 0));
      });
    } catch (error) {
      console.debug('Could not mark messages as read:', error);
    }
  };

  // Send chat message
  const handleSendChatMessage = async () => {
    if (!newChatMessage.trim() || !selectedChatUser) return;
    
    try {
      const res = await api.post('/api/chat/messages', {
        to_user_id: selectedChatUser.id,
        message: newChatMessage,
      });
      
      if (res.data.success) {
        setChatMessages([...chatMessages, res.data.data]);
        setNewChatMessage('');
      }
    } catch (error: any) {
      toast.error(t('support.sendMessageError'));
    }
  };

  // Close chat conversation
  const handleCloseChat = async () => {
    if (!selectedChatUser) return;
    
    const conversationId = [user?.id, selectedChatUser.id].sort().join('_');
    
    try {
      const res = await api.post(`/api/chat/conversations/${conversationId}/close`);
      
      if (res.data.success) {
        toast.success(t('chat.conversationClosed') || 'Konversationen avslutad');
        setConversations(conversations.filter(c => c.conversation_id !== conversationId));
        setChatView('list');
        setSelectedChatUser(null);
        setChatMessages([]);
      }
    } catch (error: any) {
      toast.error(t('chat.couldNotClose') || 'Kunde inte avsluta konversationen');
    }
  };

  // Search users with debounce
  useEffect(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    if (chatView === 'users') {
      if (chatSearch.length >= 2) {
        setIsSearching(true);
        searchTimeoutRef.current = setTimeout(async () => {
          try {
            const res = await api.get(`/api/chat/users?search=${encodeURIComponent(chatSearch)}`);
            if (res.data.success) {
              setChatUsers(res.data.data);
            }
          } catch (error) {
            console.error('Error searching users:', error);
          } finally {
            setIsSearching(false);
          }
        }, 300);
      } else if (chatSearch.length === 0) {
        setIsSearching(true);
        searchTimeoutRef.current = setTimeout(async () => {
          try {
            const res = await api.get('/api/chat/users');
            if (res.data.success) {
              setChatUsers(res.data.data);
            }
          } catch (error) {
            console.error('Error loading users:', error);
          } finally {
            setIsSearching(false);
          }
        }, 100);
      }
    }

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [chatSearch, chatView]);

  // Format date
  const formatDate = (dateStr: string): string => {
    const date = new Date(dateStr);
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    
    if (diff < 60000) return t('chat.justNow') || 'Just nu';
    if (diff < 3600000) return `${Math.floor(diff / 60000)} min`;
    if (diff < 86400000) return `${Math.floor(diff / 3600000)} h`;
    return date.toLocaleDateString('sv-SE');
  };

  const totalUnread = unreadTickets + unreadChats + unreadBroadcasts;

  return {
    // UI State
    isOpen,
    setIsOpen,
    activeTab,
    setActiveTab,
    loading,
    
    // Position & Dragging
    position,
    isDragging,
    panelPosition,
    handleDragStart,
    handleButtonClick,
    buttonRef,
    
    // Support
    supportView,
    setSupportView,
    tickets,
    selectedTicket,
    setSelectedTicket,
    newTicketSubject,
    setNewTicketSubject,
    newTicketMessage,
    setNewTicketMessage,
    newTicketPhone,
    setNewTicketPhone,
    newTicketEmail,
    setNewTicketEmail,
    newTicketRegNumber,
    setNewTicketRegNumber,
    newTicketBookingNumber,
    setNewTicketBookingNumber,
    ticketReply,
    setTicketReply,
    handleCreateTicket,
    handleTicketReply,
    handleCloseTicket,
    
    // Chat
    chatView,
    setChatView,
    conversations,
    chatUsers,
    selectedChatUser,
    chatMessages,
    newChatMessage,
    setNewChatMessage,
    chatSearch,
    setChatSearch,
    isSearching,
    handleStartChat,
    handleSendChatMessage,
    handleCloseChat,
    
    // Broadcasts
    broadcasts,
    handleMarkBroadcastRead,
    
    // FAQ
    faqItems,
    
    // Counts
    unreadTickets,
    unreadChats,
    unreadBroadcasts,
    totalUnread,
    
    // Helpers
    formatDate,
    messagesEndRef,
    supportContact,
  };
};
